import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';

import moment from 'moment';

import { getClientId, getUserId } from 'reducers';
import { fetchAdmins } from 'actions/agencyActions';
import { updateEmployeesRole } from 'actions/employeeActions';
import { fetchClientAdmins, addClientAdmin, deleteClientAdmin } from 'actions/clientActions';

import AssignAdminDialog from './AssignAdminDialog/AssignAdminDialog';
import TableWrapper from 'components/Shared/TableWrapper';
import ConfirmDeleteDialog from 'components/Shared/ConfirmDeleteDialog';
import AddTeamMemberDialog from 'components/Employees/AddTeamMemberDialog';

import { ReactComponent as AddButtonIcon } from 'assets/add-button-icon.svg';

import { ROLES } from 'constants/userConstants';
import { DATE_FORMAT_WITH_SLASH } from 'constants/commonConstants';
import {
    ADMIN_ACCOUNTS_TABLE_COLUMNS,
    DEFAULT_ADMIN_ACCOUNTS_TABLE_COLUMNS,
    ADMIN_ACCOUNTS_TABLE_SORT_QUERY_SELECTOR,
    ADMIN_ACCOUNTS_TABLE_PAGE_QUERY_SELECTOR,
    DEFAULT_ITEMS_PER_PAGE,
    INITIAL_QUERY,
    QUERY_PARAMS_MAP,
    ACTION_BUTTON_TEXT,
    DATA_SELECTOR,
} from './AdminAccountsTableConstants';

import { usePreloadTable } from 'utils/hooks';
import { convertStringToLanguageKey } from 'utils/languageUtils';

const AdminAccountsTable = ({
    agencyId,
    fetchAdmins,
    updateEmployeesRole,
    displayMode,
    clientId,
    fetchClientAdmins,
    addClientAdmin,
    deleteClientAdmin,
    userId,
}) => {
    const intl = useIntl();

    const [openAssignAdminDialog, setOpenAssignAdminDialog] = useState(false);
    const [isAddClientAdminDialogOpen, setIsAddClientAdminDialogOpen] = useState(false);
    const [openConfirmRemoveDialog, setOpenConfirmRemoveDialog] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState({});
    const [isEditMode, setIsEditMode] = useState(false);

    const tableFetchAction = displayMode === ROLES.ADMIN ? fetchAdmins.bind(null, agencyId) : fetchClientAdmins.bind(null, agencyId, clientId);
    const dataSelector = (state, page) => DATA_SELECTOR[displayMode](state, page - 1, DEFAULT_ITEMS_PER_PAGE);

    const [reload, setReload] = useState(false);
    const [isTableDataLoading, totalCount] = usePreloadTable(INITIAL_QUERY, QUERY_PARAMS_MAP, null, tableFetchAction, reload, setReload);

    const handleOpenDialog = () => {
        switch (displayMode) {
            case ROLES.ADMIN:
                setOpenAssignAdminDialog(true);
                break;
            case ROLES.CLIENT:
                setIsAddClientAdminDialogOpen(true);
                break;
            default:
                break;
        }
    }

    const handleCloseAssignAdminDialog = () => {
        setOpenAssignAdminDialog(false);
        setIsEditMode(false);
        setSelectedAdmin(null);
    };

    const handleCloseConfirmRemoveDialog = () => {
        setOpenConfirmRemoveDialog(() => false);
        setSelectedAdmin({});
    };

    const handleRemoveAdmin = async () => {
        if (displayMode === ROLES.CLIENT) {
            await deleteClientAdmin(agencyId, clientId, selectedAdmin._id)
        } else {
            const ids = [selectedAdmin._id];
            const role = ROLES.EMPLOYEE;
            await updateEmployeesRole(agencyId, ids, role, [], false);
        }
        setReload(true);
        handleCloseConfirmRemoveDialog();
    };

    const removeAdminClickHandler = admin => {
        if (!admin?.isFirst) {
            setSelectedAdmin(admin);
            setOpenConfirmRemoveDialog(true);
        }
    };

    const handleCloseAddClientAdminDialog = () => {
        setIsAddClientAdminDialogOpen(false);
    }

    const handleAdminAssignment = async (selectedEmployees) => {
        if (selectedEmployees.length > 0) {
            const ids = selectedEmployees.map(x => { x = x._id; return x; });
            const permissions = selectedEmployees[0].permissions;
            handleCloseAssignAdminDialog();
            await updateEmployeesRole(agencyId, ids, ROLES.ADMIN, permissions, false);
            setReload(true);
        }
    };

    const handleAddClientAdmin = async (values) => {
        values.addedBy = userId;
        await addClientAdmin(agencyId, clientId, values, false);
        setReload(true);
        setIsAddClientAdminDialogOpen(false);
    }

    const adjustAdminAccountsKeys = (stateData) => stateData.map((admin) => {
        admin.name = admin.name || `${admin.firstName} ${admin.lastName}`;

        return {
            name: admin.name || '-',
            position: admin.position,
            email: admin.email || '-',
            adminSince: admin.adminSince ? admin.adminSince : '-',
            registrationDate: admin.registrationDate ? moment(admin.registrationDate).format(DATE_FORMAT_WITH_SLASH) : '-',
            item: admin,
        }
    });

    const handleEditAdminClick = (admin) => {
        setSelectedAdmin(admin);
        setOpenAssignAdminDialog(true);
        setIsEditMode(true);
    };

    const actions = [
        { title: intl.formatMessage({ id: "delete" }), handleClick: removeAdminClickHandler },
        { title: intl.formatMessage({ id: "edit" }), handleClick: handleEditAdminClick },
    ];

    const getPopoverOptions = () => actions;

    return (
        <div className="admin-accounts-table-container">
            <TableWrapper
                title={intl.formatMessage({ id: "admin-accounts" })}
                columns={ADMIN_ACCOUNTS_TABLE_COLUMNS[displayMode]}
                defaultColumns={DEFAULT_ADMIN_ACCOUNTS_TABLE_COLUMNS[displayMode]}
                dataSelector={dataSelector}
                sortQuerySelector={ADMIN_ACCOUNTS_TABLE_SORT_QUERY_SELECTOR}
                adjustKeysFunction={adjustAdminAccountsKeys}
                isTableDataLoading={isTableDataLoading}
                addMargin
                withPagination
                withActions
                getActionsHandler={getPopoverOptions}
                pagesCount={Math.ceil(totalCount / DEFAULT_ITEMS_PER_PAGE)}
                pageQuerySelector={ADMIN_ACCOUNTS_TABLE_PAGE_QUERY_SELECTOR}
            >
                <TableWrapper.TableAlignedItems>
                    <TableWrapper.TableAlignedItems.LeftAligned>
                        <TableWrapper.TableAlignedItems.TableHeader />
                    </TableWrapper.TableAlignedItems.LeftAligned>
                    <TableWrapper.TableAlignedItems.RightAligned>
                        <TableWrapper.TableAlignedItems.TableActionButton
                            text={intl.formatMessage({ id: convertStringToLanguageKey(ACTION_BUTTON_TEXT[displayMode]) })}
                            icon={<AddButtonIcon />}
                            handleClick={handleOpenDialog}
                        />
                    </TableWrapper.TableAlignedItems.RightAligned>
                </TableWrapper.TableAlignedItems>
                <TableWrapper.TableContent />
            </TableWrapper>

            <AssignAdminDialog
                isOpen={openAssignAdminDialog}
                isEditMode={isEditMode}
                selectedAdmin={selectedAdmin}
                handleAdminAssignment={handleAdminAssignment}
                handleCloseDialog={handleCloseAssignAdminDialog}
            />

            <AddTeamMemberDialog
                isOpen={isAddClientAdminDialogOpen}
                handleCloseDialog={handleCloseAddClientAdminDialog}
                dialogTitle={intl.formatMessage({ id: convertStringToLanguageKey("add-new-admin") })}
                nameFieldLabelText={intl.formatMessage({ id: convertStringToLanguageKey("contact-person-name") })}
                isEmailRequired={true}
                handleRegisterMember={handleAddClientAdmin}
            />

            <ConfirmDeleteDialog
                itemToDelete="this admin"
                handleDeleteItem={() => handleRemoveAdmin()}
                openDialog={openConfirmRemoveDialog}
                handleCloseDialog={handleCloseConfirmRemoveDialog}
            />
        </div>
    )
};

const mapStateToProps = (state) => ({
    clientId: getClientId(state),
    userId: getUserId(state),
});

const mapDispatchToProps = {
    fetchAdmins,
    updateEmployeesRole,
    fetchClientAdmins,
    addClientAdmin,
    deleteClientAdmin,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminAccountsTable);
